import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import type { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import FilterContainer from '@src/components/FilterContainer';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { ResponsiveTable } from '@src/components/Table';
import { ALLOWED_ROLES, PaginationWithFirestore } from '@src/packages/utils';
import {
  WORK_JOURNEY_LIST_PAGE_SIZE,
  WORK_JOURNEY_LIST_VIEW_STORAGE_KEY,
  PATHS,
} from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import IntlMessages from '@src/util/IntlMessages';

import MarkingZoneDrawer from './components/MarkingZoneDrawer';
import WorkJourneyAssignedUsersDrawer from './components/WorkJourneyAssignedUsersDrawer';
import { useMarkingZoneDrawer } from './components/MarkingZoneDrawer/hooks/useMarkingZoneDrawer';
import WorkJourneyFilters from './components/WorkJourneyFilters';
import WorkJourneyInfo from './components/WorkJourneyInfo';
import { useWorkJourney } from './hooks/useWorkJourney';
import { useWorkJourneyList } from './hooks/useWorkJourneyList';
import styles from './styles.module.less';

function WorkJourney() {
  const history = useHistory();
  const intl = useIntl();
  const { workShift, workShiftLoading, orgId, selectedWorkJourney, setSelectedWorkJourney } =
    useWorkJourneyContext();

  const {
    filters,
    initialFilters,
    setFilters,
    toggle,
    isOpen,
    paginatedData,
    paginationState,
    loading,
    error,
    refetchSearchIds,
  } = useWorkJourneyList();

  const { columns } = useWorkJourney();
  const markingZoneDrawer = useMarkingZoneDrawer({ organizationId: orgId });

  const filterPanel = useMemo(
    () => (
      <WorkJourneyFilters
        organizationId={orgId}
        filters={filters}
        handleFilters={setFilters}
        workShift={workShift}
        workShiftLoading={workShiftLoading}
      />
    ),
    [filters, orgId, setFilters, workShift, workShiftLoading],
  );

  const filterToggleLabel = intl.formatMessage({
    id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
  });

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={initialFilters as FilterValues}
      >
        {filterPanel}
      </SecondaryPanel.Panel>
      <BoxContainer fullContent fixed bottomBorder>
        <FilterContainer
          title={<Title.Header value={<IntlMessages id="workJourney.title" />} />}
          actionButtons={[
            {
              label: filterToggleLabel,
              iconName: 'filter_alt',
              type: 'secondary',
              action: toggle,
              className: isOpen ? 'gx-border-primary' : '',
            },
            {
              label: <IntlMessages id="workJourney.markingZone" />,
              iconName: 'place',
              type: 'default',
              action: () => markingZoneDrawer.openDrawer(),
              allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.CREATE,
            },
            {
              label: <IntlMessages id="button.create" />,
              iconName: 'add',
              type: 'primary',
              action: () => history.push(PATHS.CREATE_WORK_JOURNEY),
              allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.CREATE,
            },
          ]}
        />
      </BoxContainer>
      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer fullContent error={error ?? undefined} onErrorCallback={refetchSearchIds}>
        <SecondaryPanel.Content>
          <ListViewportLayout
            viewportOffset={160}
            sidePanelOpen={isOpen}
            scrollable={false}
            footer={
              <PaginationWithFirestore
                paginationState={paginationState}
                options={{
                  showSizeChanger: true,
                  pageSizeOptions: [WORK_JOURNEY_LIST_PAGE_SIZE, '20', '50', '100'],
                }}
              />
            }
          >
            <div className={styles.tableSection}>
              <ResponsiveTable
                type="light"
                dataSource={paginatedData || []}
                columns={columns}
                loading={loading}
                className={styles.workJourneyTable}
                sortStorageKey={WORK_JOURNEY_LIST_VIEW_STORAGE_KEY}
                maxHeight="100%"
                stickyHeader
                {...({ pagination: false } as Record<string, unknown>)}
              />
            </div>
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      <WorkJourneyAssignedUsersDrawer />
      <WorkJourneyInfo
        isVisible={!!selectedWorkJourney}
        onCancel={() => setSelectedWorkJourney(null)}
      />
      <MarkingZoneDrawer drawer={markingZoneDrawer} />
    </>
  );
}

export default WorkJourney;
